import { Controller } from "@hotwired/stimulus"

/**
 * Audio Hook Picker Controller
 *
 * Picks the promo clip's in/out on a WAVEFORM. Three length modes:
 *   - auto_hook : the builder auto-picks a 15–30s window (handles hidden;
 *                 only the "auto target" field matters)
 *   - custom    : drag the in/out handles, or "Set in/out" from the playhead
 *   - full_track: the whole track (in=0, out=duration; handles locked)
 *
 * The waveform is decoded client-side (Web Audio) and drawn on a canvas. The
 * chosen in/out are written (in ms) into the hidden number fields — the form's
 * source of truth, and what the live preview reads. All Web Audio / canvas use
 * is guarded so the controller is inert (but not broken) where they're absent
 * (e.g. jsdom under test): the numeric fields + handles still work.
 *
 * Values:  audioUrl (String), durationMs (Number), mode (String)
 */
export default class extends Controller {
  static targets = [
    "stage", "canvas", "region", "inHandle", "outHandle", "playhead", "audio",
    "playIcon", "pauseIcon", "setInBtn", "setOutBtn",
    "inLabel", "outLabel", "lenLabel", "inField", "outField",
    "autoRow", "modeChip", "modeRadio",
  ]
  static values = { audioUrl: String, durationMs: Number, mode: String }

  connect() {
    this.dragging = null
    this.playing = false
    this.duration = Math.max(0, this.durationMsValue || 0)

    this.onPointerMove = this.onPointerMove.bind(this)
    this.onPointerUp = this.onPointerUp.bind(this)
    this.onTime = this.onTime.bind(this)
    window.addEventListener("pointermove", this.onPointerMove)
    window.addEventListener("pointerup", this.onPointerUp)

    if (this.hasAudioTarget) {
      this.audioTarget.addEventListener("timeupdate", this.onTime)
      this.audioTarget.addEventListener("loadedmetadata", () => this.onLoadedMetadata())
      this.audioTarget.addEventListener("ended", () => this.pause())
    }

    this.readFields()
    this.applyMode()
    this.decodeWaveform()
    this.render()
  }

  disconnect() {
    window.removeEventListener("pointermove", this.onPointerMove)
    window.removeEventListener("pointerup", this.onPointerUp)
    if (this.hasAudioTarget) this.audioTarget.removeEventListener("timeupdate", this.onTime)
    if (this.audioCtx && this.audioCtx.close) this.audioCtx.close().catch(() => {})
  }

  // ---- length mode ----

  setMode(event) {
    this.modeValue = event.target.value
    this.applyMode()
  }

  applyMode() {
    const mode = this.modeValue || "auto_hook"

    if (mode === "full_track") {
      this.inMs = 0
      this.outMs = this.duration
      this.writeFields()
    }

    this.modeChipTargets.forEach((chip) =>
      chip.classList.toggle("hookpick-mode--on", chip.dataset.mode === mode),
    )

    const custom = mode === "custom"
    const auto = mode === "auto_hook"
    this.toggleHandles(custom)
    this.toggle(this.inFieldWrapper(), custom)
    this.toggle(this.outFieldWrapper(), custom)
    if (this.hasAutoRowTarget) this.toggle(this.autoRowTarget, auto)
    if (this.hasSetInBtnTarget) this.toggle(this.setInBtnTarget, custom)
    if (this.hasSetOutBtnTarget) this.toggle(this.setOutBtnTarget, custom)
    this.render()
  }

  // ---- in/out editing ----

  // Pull in/out from the numeric fields (typing → handles move).
  syncFromFields() {
    this.readFields()
    this.render()
  }

  // Click/tap on the waveform body seeks the playhead (not when grabbing a handle).
  scrub(event) {
    if (this.dragging) return
    if (event.target.closest(".hookpick-handle")) return
    const ms = this.eventToMs(event)
    if (this.hasAudioTarget && this.duration > 0) {
      this.audioTarget.currentTime = ms / 1000
      this.render()
    }
  }

  startDragIn(event) { event.preventDefault(); this.dragging = "in" }
  startDragOut(event) { event.preventDefault(); this.dragging = "out" }

  onPointerMove(event) {
    if (!this.dragging) return
    const ms = this.eventToMs(event)
    if (this.dragging === "in") {
      this.inMs = Math.min(ms, this.outMs - 1)
    } else {
      this.outMs = Math.max(ms, this.inMs + 1)
    }
    this.render()
    this.writeFields({ silent: true })
  }

  onPointerUp() {
    if (!this.dragging) return
    this.dragging = null
    this.writeFields() // fire input once at drag end so the live preview refreshes
  }

  setIn() {
    if (!this.hasAudioTarget) return
    this.inMs = this.clamp(Math.round(this.audioTarget.currentTime * 1000))
    if (this.inMs >= this.outMs) this.outMs = this.clamp(this.inMs + 1000)
    this.render()
    this.writeFields()
  }

  setOut() {
    if (!this.hasAudioTarget) return
    this.outMs = this.clamp(Math.round(this.audioTarget.currentTime * 1000))
    if (this.outMs <= this.inMs) this.inMs = this.clamp(this.outMs - 1000)
    this.render()
    this.writeFields()
  }

  // ---- playback ----

  togglePlay() { this.playing ? this.pause() : this.play() }

  async play() {
    if (!this.hasAudioTarget) return
    try {
      await this.audioTarget.play()
      this.playing = true
    } catch (error) {
      console.error("Hook preview failed to play:", error)
      this.playing = false
    }
    this.updateIcons()
  }

  pause() {
    if (this.hasAudioTarget) this.audioTarget.pause()
    this.playing = false
    this.updateIcons()
  }

  onTime() {
    if (!this.hasPlayheadTarget || !this.hasAudioTarget) return
    const durMs = this.audioSeconds() * 1000
    if (durMs <= 0) return
    this.playheadTarget.style.left = `${this.pct(this.audioTarget.currentTime * 1000, durMs)}%`
  }

  onLoadedMetadata() {
    // Fall back to the decoded media duration when the server didn't know it.
    if (this.duration <= 0 && this.hasAudioTarget) {
      this.duration = Math.round(this.audioSeconds() * 1000)
      if (this.outMs <= 0) this.outMs = this.duration
      if (this.modeValue === "full_track") { this.inMs = 0; this.outMs = this.duration; this.writeFields() }
      this.render()
    }
  }

  // ---- helpers ----

  readFields() {
    this.inMs = this.clamp(this.parse(this.inFieldTarget))
    this.outMs = this.clamp(this.parse(this.outFieldTarget))
    if (this.outMs <= this.inMs) this.outMs = this.duration > this.inMs ? this.duration : this.inMs + 1000
  }

  writeFields({ silent = false } = {}) {
    if (this.hasInFieldTarget) this.inFieldTarget.value = String(Math.round(this.inMs))
    if (this.hasOutFieldTarget) this.outFieldTarget.value = String(Math.round(this.outMs))
    if (silent) return
    // Bubbling input so a live-preview controller listening on the form refreshes.
    this.inFieldTarget?.dispatchEvent(new Event("input", { bubbles: true }))
    this.outFieldTarget?.dispatchEvent(new Event("input", { bubbles: true }))
  }

  render() {
    if (this.duration > 0) {
      const inPct = this.pct(this.inMs, this.duration)
      const outPct = this.pct(this.outMs, this.duration)
      if (this.hasInHandleTarget) this.inHandleTarget.style.left = `${inPct}%`
      if (this.hasOutHandleTarget) this.outHandleTarget.style.left = `${outPct}%`
      if (this.hasRegionTarget) {
        this.regionTarget.style.left = `${inPct}%`
        this.regionTarget.style.width = `${Math.max(0, outPct - inPct)}%`
      }
    }
    if (this.hasInLabelTarget) this.inLabelTarget.textContent = this.fmt(this.inMs)
    if (this.hasOutLabelTarget) this.outLabelTarget.textContent = this.fmt(this.outMs)
    if (this.hasLenLabelTarget) this.lenLabelTarget.textContent = this.fmt(Math.max(0, this.outMs - this.inMs))
  }

  // Map a pointer event's X to a ms position within the track.
  eventToMs(event) {
    if (!this.hasStageTarget || this.duration <= 0) return 0
    const rect = this.stageTarget.getBoundingClientRect()
    const frac = rect.width > 0 ? (event.clientX - rect.left) / rect.width : 0
    return this.clamp(Math.round(frac * this.duration))
  }

  clamp(ms) { return Math.max(0, Math.min(this.duration || ms, Math.round(ms || 0))) }
  pct(ms, total) { return Math.max(0, Math.min(100, (ms / total) * 100)) }
  parse(field) { const n = parseInt(field?.value, 10); return Number.isFinite(n) ? n : 0 }
  audioSeconds() { return this.hasAudioTarget && Number.isFinite(this.audioTarget.duration) ? this.audioTarget.duration : 0 }

  fmt(ms) {
    const total = Math.round((ms || 0) / 1000)
    return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`
  }

  toggle(el, show) { if (el) el.classList.toggle("hookpick--hidden", !show) }
  toggleHandles(show) {
    if (this.hasInHandleTarget) this.toggle(this.inHandleTarget, show)
    if (this.hasOutHandleTarget) this.toggle(this.outHandleTarget, show)
  }

  inFieldWrapper() { return this.hasInFieldTarget ? this.inFieldTarget.closest(".wiz-field") : null }
  outFieldWrapper() { return this.hasOutFieldTarget ? this.outFieldTarget.closest(".wiz-field") : null }

  updateIcons() {
    if (this.hasPlayIconTarget) this.playIconTarget.classList.toggle("hidden", this.playing)
    if (this.hasPauseIconTarget) this.pauseIconTarget.classList.toggle("hidden", !this.playing)
  }

  // Decode the track and draw a peak waveform. Fully guarded: a missing
  // AudioContext / canvas / fetch (jsdom, or a CORS failure) just skips drawing.
  async decodeWaveform() {
    if (!this.audioUrlValue || !this.hasCanvasTarget) return
    const Ctx = window.AudioContext || window.webkitAudioContext
    if (!Ctx || typeof fetch !== "function") return
    try {
      const buf = await (await fetch(this.audioUrlValue)).arrayBuffer()
      this.audioCtx = new Ctx()
      const decoded = await this.audioCtx.decodeAudioData(buf)
      this.peaks = this.computePeaks(decoded, 600)
      if (this.duration <= 0) {
        this.duration = Math.round(decoded.duration * 1000)
        if (this.outMs <= 0) this.outMs = this.duration
        this.render()
      }
      this.drawWave()
    } catch (error) {
      console.warn("Waveform decode skipped:", error)
    }
  }

  computePeaks(decoded, buckets) {
    const data = decoded.getChannelData(0)
    const size = Math.floor(data.length / buckets) || 1
    const peaks = []
    for (let i = 0; i < buckets; i++) {
      let max = 0
      for (let j = 0; j < size; j++) {
        const v = Math.abs(data[i * size + j] || 0)
        if (v > max) max = v
      }
      peaks.push(max)
    }
    return peaks
  }

  drawWave() {
    if (!this.hasCanvasTarget || !this.peaks) return
    const canvas = this.canvasTarget
    const ctx = canvas.getContext && canvas.getContext("2d")
    if (!ctx) return
    const w = (canvas.width = this.stageTarget.clientWidth || 600)
    const h = (canvas.height = this.stageTarget.clientHeight || 96)
    ctx.clearRect(0, 0, w, h)
    ctx.fillStyle = "rgba(255,255,255,0.42)"
    const bw = w / this.peaks.length
    this.peaks.forEach((p, i) => {
      const bh = Math.max(1, p * h * 0.9)
      ctx.fillRect(i * bw, (h - bh) / 2, Math.max(1, bw - 1), bh)
    })
  }
}
